import './Style.scss';
import * as React from 'react';
import { EditIcon, Trash2Icon } from 'lucide-react';
import { Badge, Button, Form, ListGroup } from 'react-bootstrap';
import { ConfirmationModal } from 'views/Modals/ConfirmationModal';
import { getLocalization } from 'global/global';
import { deleteComment, updateComment } from 'actions/pois';
import { useAppDispatch } from 'index';
import { toast } from 'react-toastify';
import { Message } from '../';


interface Props {
  messages: Message[];
  onNoteDeleted: (id: string) => void;
  onNoteUpdated: (id: string, comment: string) => void;
}

const formatDate = (ts: number) =>
  new Date(ts).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

export default function NotesView({ messages, onNoteDeleted, onNoteUpdated }: Props) {

  const dispatch = useAppDispatch();
  const [show, setShow] = React.useState(false);
  const [id, setId] = React.useState('');
  const [mode, setMode] = React.useState<'' | 'DELETE' | 'EDIT'>('');
  const [comment, setComment] = React.useState('');

  React.useEffect(() => {
    if (!show) {
      setId('');
    }
  }, [show]);

  const deleteNote = (id: string) => {
    setId(id);
    setMode('DELETE');
    setShow(true);
  };

  const onDeleteNote = async () => {
    const deleteId = id;
    setShow(false);
    const res = await dispatch(deleteComment(deleteId));
    if (res.status === 'OK') {
      onNoteDeleted(deleteId);
    } else {
      toast(res.message, {
        type: toast.TYPE.ERROR,
        closeButton: true
      });
    }
  };

  const onUpdateComment = async () => {
    const res = await dispatch(updateComment({ id, comment }));
    if (res.status === 'OK') {
      onNoteUpdated(id, comment);
      setMode('');
      setId('');
      setComment('');
    } else {
      toast(res.message, {
        type: toast.TYPE.ERROR,
        closeButton: true
      });
    }
  };

  const confirm = show ? (
    <ConfirmationModal
      onConfirm={() => void onDeleteNote()}
      onClose={() => setShow(false)}
      localizations={{
        cancel: getLocalization('cancel'),
        confirm: getLocalization('deleteData'),
        confirmStyle: 'danger',
        header: (<>{getLocalization('confirm')}</>),
        body: (<>{getLocalization('deleteNote')}</>)
      }}
    />
  ) : null;

  return (
    <div className="crm-message-list">
      {confirm}
      <ListGroup variant="flush">
        {messages.map(msg => (
          <ListGroup.Item key={msg.id} className="px-0">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <Badge bg="secondary" data-testid="note-badge">{getLocalization('note')}</Badge>
              <small className="text-muted">
                {formatDate(msg.createdOn)} · {msg.createdBy}
              </small>
            </div>
            <div className="small">
              {mode === 'EDIT' && `${msg.id}` === id ? (
                <div className="mt-2">
                  <Form.Control
                    as={'textarea'}
                    size="sm"
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <div className="d-flex edit-footer justify-content-end mt-2">
                    <Button
                      size={'sm'}
                      variant="secondary"
                      onClick={() => {
                        setMode('');
                        setId('');
                        setComment('');
                      }}
                    >
                      {getLocalization('cancel')}
                    </Button>
                    <Button
                      size={'sm'}
                      onClick={() => void onUpdateComment()}
                    >
                      {getLocalization('update')}
                    </Button>
                  </div>
                </div>
              ) : (<>{msg.body}</>)}
            </div>
            {mode !== 'EDIT' && (
              <div className="d-flex note-footer justify-content-end mt-2">
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => {
                    setMode('EDIT');
                    setComment(msg.body);
                    setId(`${msg.id}`);
                  }}
                >
                  <EditIcon size={18} />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteNote(`${msg.id}`)}
                >
                  <Trash2Icon size={18} />
                </Button>
              </div>
            )}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </div>
  );
}
